import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2, PlusCircle, ChevronUp } from "react-feather";
import { Table } from "antd";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import BranchModal from "../../core/modals/peoples/BranchModal";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader, setBranchData, setCurrentBranch } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useToast } from "../../Toaster/ToastContext";
// import { Switch } from "antd";
import { apiClient } from "../../services/apiClient";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const Branches = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const branchData = useSelector((state) => state.branchdata);
  const rawCurrentBranch = useSelector(
    (state) => state.currentBranch,
    (prev, next) => prev?.id === next?.id
  );
  const currentBranch = useMemo(() => rawCurrentBranch, [rawCurrentBranch?.id]);

  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const MySwal = withReactContent(Swal);
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalCount: 0,
  });

  const fetchBranches = useCallback(
    async (currentPage = 1, pageSize = 20) => {
    if (hasFetched.current && !refresh && !loading) {
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post("/paginate-branch", {
        pageIndex: currentPage - 1,
        pageSize,
        previousPageIndex: 0,
      });
      const result = await response;
      // console.log("fetchBranches: Response received", result);
      if (result.success) {
        const transformedData = result.data.map((branch) => ({
          ...branch,
          name: branch.name || "",
          email: branch.email || "",
          phone: branch.phone || "",
          address: branch.address || "",
          status: branch.status || "active",
        }));
        setBranches(transformedData);
        setFilteredBranches(transformedData);
        dispatch(setBranchData(transformedData));
        setPagination({
          currentPage: result.currentPage || currentPage,
          pageSize: result.pageSize || pageSize,
          totalPages: result.totalPages || 1,
          totalCount: result.totalCount || transformedData.length,
        });
      } else {
        showToast(
          result.message || "Failed to fetch branches",
          "error",
          "Branches"
        );
      }
    } catch (error) {
      console.error("fetchBranches: Error", error);
      showToast(
        error.message || "Failed to fetch branches",
        "error",
        "Branches"
      );
    } finally {
      setLoading(false);
      setRefresh(false);
      hasFetched.current = true;
    }
  }, [showToast, dispatch, refresh]);

  useEffect(() => {
    if (!hasFetched.current || refresh) {
      fetchBranches(pagination.currentPage, pagination.pageSize);
    }
  }, [fetchBranches, refresh, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    setBranches(branchData);
    setFilteredBranches(branchData);
    // console.log("useEffect: Synced branches with branchData", branchData);
  }, [branchData]);

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    try {
      if (!storedPermissions) return;

      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);

      let parsedPermissions = getPermissions.permissions || getPermissions;

      // 🔹 Find People section
      const peopleSection = parsedPermissions.find(
        (item) => item.title === "People"
      );

      // 🔹 Find Branches page
      const branchesPage = peopleSection?.childPage?.find(
        (child) => child.title === "Branches"
      );

      if (branchesPage) {
        setPermissions({
          create: branchesPage.create || false,
          read: branchesPage.read || false,
          update: branchesPage.update || false,
          delete: branchesPage.delete || false,
        });
      }
    } catch (error) {
      console.error("Error parsing permissions for Branches:", error);
      showToast("Failed to load branch permissions", "error", "Permissions");
    }
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    const filtered = branches.filter((item) =>
      item.name.toLowerCase().includes(term)
    );
    setFilteredBranches(filtered);
  };

  const handleToggleStatus = async (branch) => {
    const newStatus = branch.status === "active" ? "inactive" : "active";
    try {
      setLoading(true);
      const response = await apiClient.post("/store-update-branch", {
        id: branch.id,
        status: newStatus === "active" ? "active" : "inactive",
        name: branch.name,
        email: branch.email,
        phone: branch.phone,
        address: branch.address,
      });
      if (response.success) {
        showToast(
          `Branch status updated to ${newStatus}`,
          "success",
          "Branch Operation"
        );
        // Update the local state to reflect the change
        const updatedBranches = branches.map((b) =>
          b.id === branch.id ? { ...b, status: newStatus } : b
        );
        setBranches(updatedBranches);
        setFilteredBranches(updatedBranches);
        dispatch(setBranchData(updatedBranches));
      } else {
        showToast(
          response.message || "Failed to update branch status",
          "error",
          "Branch Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Failed to update branch status",
        "error",
        "Branch Operation"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddBranch = () => dispatch(setCurrentBranch(null));

  const handleEditBranch = useCallback(
    (branch) => {
      // console.log("handleEditBranch: Setting currentBranch", branch);
      dispatch(setCurrentBranch(branch));
    },
    [dispatch]
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const showConfirmationAlert = useCallback(
    async (record) => {
      MySwal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        showCancelButton: true,
        confirmButtonColor: "#00ff00",
        confirmButtonText: "Yes, delete it!",
        cancelButtonColor: "#ff0000",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setLoading(true);
            // const response = await deleteBranch(record.id);
            // const data = await response.json();
            const data = await apiClient.post("/delete-branch", {
              id: record.id,
            });
            if (data.success) {
              showToast(
                data.msg || "Branch deleted successfully",
                "success",
                "Branches"
              );
              setRefresh(true);
              hasFetched.current = false;
              fetchBranches(pagination.currentPage, pagination.pageSize);
            } else {
              showToast(
                data.msg || "Failed to delete branch",
                "error",
                "Branches"
              );
            }
          } catch (error) {
            console.error("showConfirmationAlert: Error", error);
            showToast(
              error.message || "Failed to delete branch",
              "error",
              "Branches"
            );
          } finally {
            setLoading(false);
          }
        }
      });
    },
    [showToast, MySwal, fetchBranches, pagination.currentPage, pagination.pageSize]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Name",
        dataIndex: "name",
        render: (text) => <span className="wrap-text">{text}</span>,
        sorter: (a, b) => a.name.length - b.name.length,
        width: 200,
      },
      {
        title: "Email",
        dataIndex: "email",
        render: (text) => <span className="wrap-text">{text}</span>,
        sorter: (a, b) => a.email.length - b.email.length,
        width: 200,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        render: (text) => <span className="wrap-text">{text}</span>,
        sorter: (a, b) => a.phone.length - b.phone.length,
        width: 150,
      },
      {
        title: "Address",
        dataIndex: "address",
        render: (text) => <span className="wrap-text">{text}</span>,
        sorter: (a, b) => a.address.length - b.address.length,
        width: 200,
      },
    ];

    // ✅ Only include Status column if read permission is true
    if (permissions.read) {
      baseColumns.push({
        title: "Status",
        dataIndex: "status",
        render: (text, record) => (
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={text === "active"}
              onChange={() => handleToggleStatus(record)}
            />
            <span className="toggle-slider"></span>
          </label>
        ),
        sorter: (a, b) => a.status.length - b.status.length,
        width: 100,
      });
    }

    // Actions column: only if read OR delete permission
    if (permissions.read || permissions.delete) {
      baseColumns.push({
        title: "Action",
        dataIndex: "action",
        render: (_, record) => (
          <td className="action-table-data">
            <div className="edit-delete-action">
              {permissions.read && (
                <Link
                  className="me-2 p-2"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#branch-modal"
                  onClick={() => handleEditBranch(record)}
                >
                  <Edit className="feather-edit" />
                </Link>
              )}
              {permissions.delete && (
                <Link
                  className="confirm-text p-2"
                  to="#"
                  onClick={() => showConfirmationAlert(record)}
                >
                  <Trash2 className="feather-trash-2" />
                </Link>
              )}
            </div>
          </td>
        ),
        width: 150,
      });
    }

    return baseColumns;
  }, [handleEditBranch, showConfirmationAlert, permissions]);

  const { currentPage, pageSize, totalPages, totalCount } = pagination;
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(startItem + pageSize - 1, totalCount);

  const pagesToShow = [];
  if (totalPages > 0) {
    if (totalPages <= 2) {
      for (let i = 1; i <= totalPages; i++) {
        pagesToShow.push(i);
      }
    } else {
      let middle = [];
      if (currentPage === 1) {
        middle = [1, 2];
      } else if (currentPage === totalPages) {
        middle = [totalPages - 1, totalPages];
      } else {
        middle = [currentPage, currentPage + 1];
      }

      if (middle[0] > 1) {
        pagesToShow.push(1);
      }
      if (middle[0] > 2) {
        pagesToShow.push("...");
      }
      middle.forEach((p) => pagesToShow.push(p));
      if (middle[1] < totalPages - 1) {
        pagesToShow.push("...");
      }
      if (middle[1] < totalPages) {
        pagesToShow.push(totalPages);
      }
    }
  }

  const changePage = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
    hasFetched.current = false;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Branch List</h4>
              <h6>Manage Your Branches</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            {/* <Link
              to="#"
              className="btn btn-added"
              data-bs-toggle="modal"
              data-bs-target="#branch-modal"
              onClick={handleAddBranch}
            >
              <PlusCircle className="me-2" /> Add New Branch
            </Link> */}
            {permissions.create && (
              <Link
                to="#"
                className="btn btn-added"
                onClick={handleAddBranch}
                data-bs-toggle="modal"
                data-bs-target="#branch-modal"
              >
                <PlusCircle className="me-2" />
                Add New Branch
              </Link>
            )}
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by Name"
                    className="form-control form-control-sm formsearch"
                    onChange={handleSearch}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew"
                columns={columns}
                dataSource={filteredBranches}
                rowKey={(record) => record.id}
                loading={loading}
                rowSelection={null}
                pagination={false}
              />
            </div>
            {totalCount > 0 && (
              <div className="custom-pagination d-flex justify-content-between align-items-center mt-4 mb-4">
                <div className="pagination-left">
                  Showing {startItem} - {endItem} of {totalCount}
                </div>
                <div className="pagination-right d-flex align-items-center">
                  <button
                    className="btn btn-sm btn-outline-secondary me-2"
                    disabled={currentPage === 1}
                    onClick={() => changePage(currentPage - 1)}
                  >
                    <LeftOutlined size={12} />
                  </button>
                  {pagesToShow.map((page, index) => (
                    <span key={index} className="me-2">
                      {page === "..." ? (
                        <span className="pagination-ellipsis">...</span>
                      ) : (
                        <button
                          className={`btn btn-sm ${
                            page === currentPage
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => changePage(page)}
                        >
                          {page}
                        </button>
                      )}
                    </span>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-secondary ms-2"
                    disabled={currentPage === totalPages}
                    onClick={() => changePage(currentPage + 1)}
                  >
                    <RightOutlined size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BranchModal
        branch={currentBranch}
        onSave={() => setRefresh(true)}
        canUpdate={permissions.update}
        canCreate={permissions.create}
      />
    </div>
  );
};

export default Branches;
